import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines } from "../components/Reveal";
import { api, asList } from "../lib/api";
import useSeo from "../lib/useSeo";

const groupOf = (k = "") => (k.includes("Song") ? "Songs" : k.includes("Essay") ? "Essays" : k);

export default function Explore() {
  useSeo("Explore", "Essays, manifestos, meditations and songs from Outlaw Creative. A curated index of written work and ideas.");
  const [pieces, setPieces] = useState([]);
  const [filter, setFilter] = useState("All");
  const [hover, setHover] = useState(null);
  const [limit, setLimit] = useState(30);

  useEffect(() => { api.get("/pieces").then((r) => setPieces(asList(r))).catch(() => setPieces([])); }, []);

  const groups = useMemo(() => ["All", ...Array.from(new Set(pieces.map((p) => groupOf(p.kind))))], [pieces]);
  const shown = filter === "All" ? pieces : pieces.filter((p) => groupOf(p.kind) === filter);

  return (
    <div className="px-5 sm:px-8 lg:px-12 pt-32 pb-24" data-testid="explore-page">
      <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
        <div className="lg:col-span-8">
          <p className="eyebrow mb-5">01 / Explore</p>
          <h1 className="f-display font-black uppercase text-[20vw] sm:text-[14vw] lg:text-[10vw] leading-[0.82]">
            <MaskLines onMount lines={["The", <span key="i" className="outline-text">Index</span>]} />
          </h1>
        </div>
        <p className="lg:col-span-4 self-end f-serif italic text-xl sm:text-2xl text-[#cfcec8] leading-snug">
          Written work and ideas. Essays, manifestos, meditations, lyrics. Each one a coordinate.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by form">
        {groups.map((g) => (
          <button
            key={g}
            role="tab"
            aria-selected={filter === g}
            onClick={() => { setFilter(g); setLimit(30); }}
            className={`f-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${filter === g ? "bg-[var(--bone)] text-[var(--ink)] border-[var(--bone)]" : "border-[var(--line)] text-[#cfcec8] hover:border-[var(--bone)]"}`}
            data-testid={`explore-filter-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {g}
          </button>
        ))}
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
        <ol className="border-t border-[var(--bone)]" data-testid="explore-list">
          {shown.slice(0, limit).map((p, i) => (
            <motion.li
              key={p.slug}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: Math.min(i % 30, 10) * 0.04 }}
              className="border-b border-[var(--line)]"
              onMouseEnter={() => setHover(p)}
            >
              <Link to={`/explore/${p.slug}`} className="group grid grid-cols-12 gap-3 py-6 sm:py-8 items-baseline" data-testid={`explore-piece-${p.slug}`}>
                <span className="col-span-2 sm:col-span-1 f-mono text-xs text-[var(--ash)]">{String(i + 1).padStart(2, "0")}</span>
                <span className="col-span-10 sm:col-span-8">
                  <span className="block f-display font-black uppercase text-3xl sm:text-5xl leading-[0.92] group-hover:text-[var(--signal)] transition-colors duration-300">{p.title}</span>
                  <span className="block f-serif italic text-base sm:text-lg text-[#bdbcb5] mt-2">{p.dek}</span>
                </span>
                <span className="col-span-12 sm:col-span-3 sm:text-right f-mono text-[11px] uppercase text-[var(--ash)] flex sm:justify-end items-center gap-2 pl-[16.66%] sm:pl-0">
                  {p.kind}{p.read_minutes ? ` \u00b7 ${p.read_minutes} min read` : ""}
                  <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            </motion.li>
          ))}
        </ol>
        {shown.length > limit && (
          <button onClick={() => setLimit((l) => l + 30)} className="mt-8 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-6 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors" data-testid="explore-show-more">Show more ({shown.length - limit} left)</button>
        )}
        </div>
        <aside className="hidden lg:block lg:col-span-4">
          <div className="sticky top-28 border border-[var(--line)] p-6" data-testid="explore-preview">
            <svg viewBox="0 0 200 200" className="w-full" aria-hidden="true">
              <rect x="0.5" y="0.5" width="199" height="199" fill="none" stroke="var(--line)" />
              <circle cx="100" cy="100" r="80" fill="none" stroke="var(--line)" />
              <line x1="0" y1="100" x2="200" y2="100" stroke="var(--line)" />
              <line x1="100" y1="0" x2="100" y2="200" stroke="var(--line)" />
              {pieces.map((p, i) => {
                const a = (i / Math.max(pieces.length, 1)) * Math.PI * 2;
                const on = hover?.slug === p.slug;
                return <circle key={p.slug} cx={100 + Math.cos(a) * 80} cy={100 + Math.sin(a) * 80} r={on ? 6 : 2.5} fill={on ? "var(--signal)" : "var(--bone)"} style={{ transition: "r .4s" }} />;
              })}
            </svg>
            <p className="f-mono text-[11px] text-[var(--ash)] mt-6">{hover ? `${hover.kind} / no. ${String(hover.order).padStart(2, "0")}` : "Hover a piece to locate it"}</p>
            <p className="f-display font-bold uppercase text-3xl mt-2 leading-none min-h-[2rem]">{hover?.title || "\u2014"}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
